"use client";

import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { LuCheck, LuChevronsUpDown } from "react-icons/lu";
import { toast } from "sonner";

import { fetchLastUsedMovedAccounts } from "@/actions/expense";
import { fetchMovedAccounts } from "@/actions/movedAccounts";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { FormControl, FormMessage } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { cn } from "@/lib/utils";

interface IMovedAccountProps {
    isPending: boolean;
    value: string | undefined;
    onChange: (journal: string | undefined) => void;
}

export const MovedAccounts = ({ value, onChange, isPending }: IMovedAccountProps) => {
    const t = useTranslations("expense");

    const user = useCurrentUser();

    const [ isOpen, setIsOpen ] = useState(false);

    const handleSelect = (movedAccountId: string) => {
        onChange(movedAccountId);
        setIsOpen(false);
    };

    const {
        data: movedAccounts,
        error,
        isLoading,
    } = useQuery({
        queryKey: [ "movedAccounts" ],
        queryFn: fetchMovedAccounts,
    });

    const {
        data: LastmovedAccounts,
        error: errorLastmovedAccounts,
        isLoading: isLoadingLastmovedAccounts,
    } = useQuery({
        queryKey: [ "lastUsedMovedAccounts", user, user?.id ],
        queryFn: () => {
            if (user) {
                return fetchLastUsedMovedAccounts(user.id);
            }
            return Promise.resolve([]);
        },
        enabled: !!user,
    });

    if (error) {
        toast.error(t("error.fetchMovedAccount"));
        return null;
    }


    if (errorLastmovedAccounts) {
        toast.error(t("error.fetchLastMovedAccount"));
        return null;
    }

    if (isLoading && isLoadingLastmovedAccounts) {
        return <Skeleton className="h-[40px] w-full" />;
    }


    return (
        <>
            {movedAccounts ? (
                <>
                    {!user ? (
                        <Skeleton className="h-[40px] w-full" />
                    ) : (
                        <Popover open={ isOpen } onOpenChange={ setIsOpen }>
                            <PopoverTrigger asChild>
                                <FormControl>
                                    <Button
                                        disabled={ isPending }
                                        variant="outline"
                                        role="combobox"
                                        className={ cn("w-full justify-between", !value && "text-muted-foreground") }
                                    >
                                        {value
                                            ? movedAccounts.find(movedAccount => movedAccount.id === value)?.accountCode
                                            : t("selectNumberMovedAccount")}
                                        <LuChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
                                    </Button>
                                </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="p-0">
                                <Command>
                                    <CommandInput placeholder={ t("searchMovedAccount") } />
                                    <CommandList>
                                        <CommandEmpty>{t("noMovedAccountFound")}</CommandEmpty>
                                        {LastmovedAccounts && LastmovedAccounts.length > 0 ? (
                                            <CommandGroup heading={ t("history") }>
                                                {LastmovedAccounts.map(movedAccount => (
                                                    <CommandItem
                                                        key={ movedAccount.id }
                                                        value={ `${ movedAccount.accountCode } - ${ movedAccount.name }` }
                                                        onSelect={ () => handleSelect(movedAccount.id) }
                                                        className="text-primary"
                                                    >
                                                        {movedAccount.accountCode} - {movedAccount.name}
                                                        <LuCheck className={ cn("ml-auto", movedAccount.id === value ? "opacity-100" : "opacity-0") } />
                                                    </CommandItem>
                                                ))}
                                                <CommandSeparator />
                                            </CommandGroup>
                                        ) : null}
                                        <CommandGroup heading={ t("account") }>
                                            {movedAccounts.map(movedAccount => (
                                                <CommandItem
                                                    key={ movedAccount.id }
                                                    value={ `${ movedAccount.accountCode } - ${ movedAccount.name }` }
                                                    onSelect={ () => handleSelect(movedAccount.id) }
                                                >
                                                    {movedAccount.accountCode} - {movedAccount.name}
                                                    <LuCheck className={ cn("ml-auto", movedAccount.id === value ? "opacity-100" : "opacity-0") } />
                                                </CommandItem>
                                            ))}
                                        </CommandGroup>
                                    </CommandList>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    )}
                    <FormMessage />
                </>
            ) : null}
        </>
    );
};
